/**
 * @prettier
 */
import React from 'react';

/**
 * Radio with active styling
 * @param {Object} props
 * @param {number | string} props.width
 * @param {number | string} props.size
 * @param {string} props.color
 */
const RadioActive = ({ size, width, color }) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
      fill="white"
      stroke={color}
      strokeWidth={width}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19Z"
      fill={color}
    />
  </svg>
);

/**
 * Radio with inactive styling
 * @param {Object} props
 * @param {number | string} props.width
 * @param {number | string} props.size
 * @param {string} props.color
 * @param {(event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void} [props.onClick]
 */
const RadioInactive = ({ width, size, color, onClick }) => (
  <svg
    onClick={onClick}
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
      fill="white"
      stroke={color}
      strokeWidth={width}
    />
  </svg>
);

/**
 * Radio with disabled styling
 * @param {Object} props
 * @param {number | string} props.width
 * @param {number | string} props.size
 * @param {string} props.color
 * @param {(event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void} [props.onClick]
 */
const RadioDisabled = ({ width, size, color, onClick }) => (
  <svg
    onClick={onClick}
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
      fill="#F8F8F8"
      stroke={color}
      strokeWidth={width}
    />
  </svg>
);

/**
 * Radio button to from new design
 * @param {Object} props
 * @param {boolean} [props.active] - Active status
 * @param {boolean} [props.disabled] - Disabled status
 * @param {(event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void} [props.onClick] - onClick event handler
 * @param {number | string} [props.size] - Size of the element, defaults to 24
 * @param {number | string} [props.width] - Width of the element, defaults to 2
 * @param {string} [props.activeColor] - Default color for active status, defaults to #0075AE
 * @param {string} [props.inactiveColor] - Default color for inactive status, defaults to #D9D9D9
 */
const RadioButton = ({
  active = false,
  disabled = false,
  onClick,
  size = 24,
  width = 2,
  activeColor = '#0075AE',
  inactiveColor = '#D9D9D9',
}) => {
  if (disabled) return <RadioDisabled size={size} color={inactiveColor} width={width} />;
  if (active) return <RadioActive size={size} color={activeColor} width={width} />;
  return <RadioInactive onClick={onClick} size={size} color={inactiveColor} width={width} />;
};

export default RadioButton;
